
import {defineComponent, nextTick, PropType, ref, watch} from 'vue'
import {CtCatalogue, CtCatalogueProduct} from '@/models/interfaces/content-type'
import {useStore} from 'vuex'
import EditableBlock from '@/components/EditableBlock.vue'
import EditableBlockInput from '@/components/EditableBlockInput.vue'
import {QTable, useQuasar} from 'quasar'
import SkuSettingsField from '@/components/SkuSettingsField.vue'
import EyeIcon from '@/components/Icons/EyeIcon.vue'
import {ContentType} from '@/services/directr'
import {SkuController, SkuCtrl} from '@/pages/Manage/Catalogues/utils'
import DButton from '@/components/base/DButton.vue'


export default defineComponent({
  name: 'SkuSettings',
  components: {EyeIcon, SkuSettingsField, EditableBlockInput, EditableBlock, DButton},
  props: {
    catalogue: {
      type: Object as PropType<CtCatalogue>,
      required: true
    },
    readonly: {
      type: Boolean
    }
  },
  emits: ['update:catalogue'],
  setup (props, {emit}) {
    const $q = useQuasar()
    const $store = useStore()
    const $directr = $store.getters['Services/Directr']

    const showSkuList = ref(false)
    const tableRef = ref<QTable | null>(null)
      watch(() => showSkuList.value, async (show) => {
      if (show) {
        await nextTick()
        tableRef.value?.requestServerInteraction()
      }
    })

    const rows = ref([])
    const skuColumns = [
      {
        name: 'product_name',
        required: true,
        label: 'Product name',
        align: 'left',
        field: 'title',
        sortable: false,
        headerStyle: 'width: 80%',
        style: 'text-overflow: ellipsis; overflow: hidden;'
      },
      { name: 'sku', align: 'right', label: 'SKU', field: 'sku', sortable: false },
    ]
    const pagination = ref({
      sortBy: 'product_name',
      descending: false,
      page: 1,
      rowsPerPage: 10,
      rowsNumber: 0,
    })
    const totalCount = async () => {
      const { items = [{ count: 0 }] as [{ count: number }] } = await $directr.get(ContentType.CatalogueProduct, [{
        select: ['COUNT(*)'],
        from: [ContentType.CatalogueProduct],
        where: {
          [`${ContentType.CatalogueProduct}.catalogue`]: props.catalogue.sys.id,
        },
      }], { orm: true })
      pagination.value.rowsNumber = items[0].count
    }
    void totalCount()

    const fetchFromServer = async (tableState, editState) => {
      if (tableState?.pagination) {
        pagination.value = tableState?.pagination
      }
      const { items = [] as CtCatalogueProduct[] } = await $directr.get(ContentType.CatalogueProduct, [{
        select: [`${ContentType.CatalogueProduct}.title as cp_title`, `${ContentType.CatalogueProduct}.sku`, `${ContentType.Product}.title as p_title`],
        from: [ContentType.CatalogueProduct],
        leftJoin: [`${ContentType.CatalogueProduct}.id`, `${ContentType.Product}.__id`],
        where: {
          [`${ContentType.CatalogueProduct}.catalogue`]: props.catalogue.sys.id,
        },
        offset: (pagination.value.page - 1) * pagination.value.rowsPerPage,
        limit: pagination.value.rowsPerPage,
      }], { orm: true })
      const skuController = SkuController(editState)
      rows.value = items.map(item => ({ title: item.cp_title || item.p_title, sku: skuController.display(item.sku) }))
    }


    return {
      tableRef,
      rows,
      pagination,
      showSkuList,
      SkuController,
      skuColumns,
      fetchFromServer,
      clipped: () => {
        $q.notify({ message: 'Copied to clipboard', position: 'top', classes: 'custom-notify' })
      },
    }
  },

})
